'use client';

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Stack, TextField, CircularProgress } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { createWorkshop, updateWorkshop, getWorkshopById } from '../../../stores/workshopStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import { useLanguage } from '../../../utils/i18n';

const WorkshopAddEdit = forwardRef(
  ({ id = null, onClose = null, onSuccess = null, onError = null, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const [initialValues, setInitialValues] = useState({
      company: '',
      company_name: '',
      name: '',
      cp_name: '',
      cp_number: '',
      address: ''
    });

    const [loading, setLoading] = useState(Boolean(id));
    const formikRef = useRef();

    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.submitForm?.()
    }));
    useEffect(() => {
      let mounted = true;
      const fetch = async () => {
        if (!id) {
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          const data = await getWorkshopById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              company: data.company != null ? String(data.company) : '',
              company_name: data.company_name ?? data.join?.company_name ?? '',
              name: data.name ?? '',
              cp_name: data.cp_name ?? '',
              cp_number: data.cp_number ?? data.cp_number ?? '',
              address: data.address ?? ''
            });
          }
        } catch (err) {
          console.error('Failed to load Workshop:', err);
          onError && onError(t('workshop.failedLoadData', 'Failed to load workshop data'));
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => {
        mounted = false;
      };
    }, [id, onError, t]);
    useEffect(() => {
      if (id) return;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;

      const firstId = stored.split(',')[0]?.trim();
      if (firstId) {
        setInitialValues((prev) => ({
          ...prev,
          company: Number(firstId) || firstId
        }));
      }
    }, [id]);

    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required(t('workshop.validationCompany', 'Company is required')),

      name: Yup.string()
        .required(t('workshop.validationName', 'Name is required'))
        .min(3, t('workshop.validationNameLength', 'Name field must be between 3-30 chars!'))
        .max(30, t('workshop.validationNameLength', 'Name field must be between 3-30 chars!')),

      cp_name: Yup.string()
        .required(t('workshop.validationCpName', 'CP Name is required'))
        .min(3, t('workshop.validationCpNameLength', 'CP Name field must be between 3-30 chars!'))
        .max(30, t('workshop.validationCpNameLength', 'CP Name field must be between 3-30 chars!')),

      cp_number: Yup.string()
        .required(t('workshop.validationCpPhone', 'CP Phone is required'))
        .matches(/^[0-9]+$/, t('workshop.validationCpPhoneFormat', 'Please enter a valid number for CP Phone field'))
        .min(10, t('workshop.validationCpPhoneLength', 'CP Phone field must be between 10-15 number!'))
        .max(15, t('workshop.validationCpPhoneLength', 'CP Phone field must be between 10-15 number!')),

      address: Yup.string()
        .required(t('workshop.validationAddress', 'Address is required'))
        .min(10, t('workshop.validationAddressLength', 'Address field must be between 10-100 chars!'))
        .max(100, t('workshop.validationAddressLength', 'Address field must be between 10-100 chars!'))
    });

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const payload = {
          ...values,
          id: id ?? 0,
          company: Number(values.company),
          cp_number: values.cp_number
        };

        if (id) {
          await updateWorkshop(id, payload);
          onSuccess && onSuccess(t('workshop.updatedSuccess', 'Workshop updated successfully'));
        } else {
          await createWorkshop(payload);
          onSuccess && onSuccess(t('workshop.createdSuccess', 'Workshop created successfully'));
        }
        onClose && onClose();
      } catch (err) {
        const errorMessage = err?.response?.data?.message || err?.message || t('workshop.submitFailed', 'Submit failed');
        onError && onError(errorMessage ?? t('workshop.submitFailed', 'Submit failed'));
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name',
      searchPlaceholder: t('general.search', 'Search...')
    });

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, handleChange, handleBlur, setFieldValue, touched, errors }) => (
            <form onSubmit={formikRef.current?.handleSubmit}>
              <Stack spacing={3}>
                <PickerField
                  label={t('workshop.company', 'Company *')}
                  {...companyPickerParams}
                  value={values.company || ''}
                  onChange={(val) => {
                    setFieldValue('company', val || '');
                    const opt =
                      companyPickerParams.options.find((o) => String(o.value) === String(val)) ||
                      companyPickerParams.listOptions?.find((o) => String(o.value) === String(val));
                    setFieldValue('company_name', opt?.raw?.name ?? opt?.label ?? '');
                  }}
                  error={touched.company && Boolean(errors.company)}
                  helperText={touched.company && errors.company}
                />

                <TextField
                  label={t('workshop.workshopNameRequired', 'Workshop Name *')}
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('workshop.cpName', 'CP Name')}
                    name="cp_name"
                    value={values.cp_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.cp_name && Boolean(errors.cp_name)}
                    helperText={touched.cp_name && errors.cp_name}
                  />
                  <TextField
                    label={t('workshop.cpNumber', 'CP Number')}
                    name="cp_number"
                    value={values.cp_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.cp_number && Boolean(errors.cp_number)}
                    helperText={touched.cp_number && errors.cp_number}
                  />
                </Stack>

                <TextField
                  label={t('workshop.addressRequired', 'Address *')}
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  multiline
                  rows={3}
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address && errors.address}
                />
              </Stack>
            </form>
          )}
        </Formik>
      </Box>
    );
  }
);

export default WorkshopAddEdit;
